/**
 * DPL DEW Tenant Control Center
 * Production-grade tenant management with advanced filtering and actions
 */

'use client';

import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Building2,
  Users,
  CreditCard,
  Zap,
  FileText,
  AlertTriangle,
  MoreHorizontal,
  Search,
  Filter,
  Eye,
  Settings,
  Ban,
  LogOut,
  RefreshCw,
  Download,
  Shield,
  TrendingUp,
  TrendingDown,
  UserX,
  CheckCircle2,
  MinusCircle
} from 'lucide-react';
import { adminApi } from '@/admin/services/api';
import { adminAuth } from '@/admin/lib/auth';
import { toast } from 'sonner';
import type { 
  TenantOverview, 
  TenantFilters,
  PaginatedResponse,
  AdminRole 
} from '@/admin/types';

const PLAN_COLORS = {
  starter: 'bg-slate-600/20 text-muted-foreground border-slate-600/30',
  pro: 'bg-emerald-600/20 text-emerald-400 border-emerald-600/30',
  enterprise: 'bg-purple-600/20 text-purple-400 border-purple-600/30'
};

const STATUS_COLORS = {
  active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  trial: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  expired: 'bg-red-500/20 text-red-400 border-red-500/30',
  inactive: 'bg-slate-600/20 text-muted-foreground border-slate-600/30'
};

const HEALTH_COLORS = {
  high: 'text-emerald-500',
  medium: 'text-yellow-500',
  low: 'text-red-500'
};

export function TenantControlCenter() {
  const [tenants, setTenants] = useState<TenantOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<TenantFilters>({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    total_pages: 0
  });
  const [selectedTenant, setSelectedTenant] = useState<TenantOverview | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const [isIndustryDialogOpen, setIsIndustryDialogOpen] = useState(false);
  const [isMaxUsersDialogOpen, setIsMaxUsersDialogOpen] = useState(false);
  const [actionTenantId, setActionTenantId] = useState<string | null>(null);
  const [newIndustry, setNewIndustry] = useState('');
  const [newMaxUsers, setNewMaxUsers] = useState<number | ''>('');

  const loadTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };
      
      const response = await adminApi.getTenants(params);
      
      if (response.success && response.data) {
        setTenants(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleTenantAction = async (tenantId: string, action: string, reason?: string) => {
    try {
      setActionLoading(`${tenantId}-${action}`);
      
      switch (action) {
        case 'suspend':
          await adminApi.suspendTenant(tenantId, reason || 'Admin action');
          break;
        case 'activate':
          await adminApi.activateTenant(tenantId);
          break;
        case 'upgrade_pro':
          const upgradeRes = await adminApi.upgradeTenantToPro(tenantId);
          if (!upgradeRes.success) throw new Error(upgradeRes.error as string);
          toast.success('Tenant upgraded to Pro successfully');
          break;
        case 'downgrade_starter':
          const downgradeRes = await adminApi.downgradeTenantToStarter(tenantId);
          if (!downgradeRes.success) throw new Error(downgradeRes.error as string);
          toast.success('Tenant downgraded to Starter successfully');
          break;
        case 'impersonate':
          const response = await adminApi.impersonateTenant(tenantId, reason || 'Admin investigation');
          if (response.success && response.data) {
            // Open tenant in new window with impersonation token
            const newWindow = window.open(
              `${window.location.origin}?impersonation_token=${response.data.impersonation_token}`,
              '_blank'
            );
            if (newWindow) {
              newWindow.focus();
            }
          }
          break;
        case 'reset_usage':
          await adminApi.resetTenantUsage(tenantId);
          break;
        case 'force_logout':
          await adminApi.forceLogoutTenant(tenantId);
          break;
        case 'sync_billing':
          await adminApi.syncTenantBilling(tenantId);
          break;
        case 'export':
          const exportResponse = await adminApi.exportTenantData(tenantId);
          if (exportResponse.success && exportResponse.data) {
            window.open(exportResponse.data.download_url, '_blank');
          }
          break;
      }
      
      // Refresh tenants list
      await loadTenants();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${action} tenant`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleOpenIndustryDialog = (tenantId: string) => {
    setActionTenantId(tenantId);
    setNewIndustry('');
    setIsIndustryDialogOpen(true);
  };

  const handleOpenMaxUsersDialog = (tenantId: string) => {
    setActionTenantId(tenantId);
    setNewMaxUsers('');
    setIsMaxUsersDialogOpen(true);
  };

  const submitIndustryChange = async () => {
    if (!actionTenantId || !newIndustry) return;
    try {
      setActionLoading(`${actionTenantId}-industry`);
      const res = await adminApi.updateTenantIndustry(actionTenantId, newIndustry);
      if (!res.success) throw new Error(res.error as string);
      toast.success('Industry updated successfully');
      setIsIndustryDialogOpen(false);
      loadTenants();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update industry');
    } finally {
      setActionLoading(null);
    }
  };

  const submitMaxUsersChange = async () => {
    if (!actionTenantId || newMaxUsers === '') return;
    try {
      setActionLoading(`${actionTenantId}-max_users`);
      const res = await adminApi.updateTenantMaxUsers(actionTenantId, Number(newMaxUsers));
      if (!res.success) throw new Error(res.error as string);
      toast.success('Max users updated successfully');
      setIsMaxUsersDialogOpen(false);
      loadTenants();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update max users');
    } finally {
      setActionLoading(null);
    }
  };

  const handleFilterChange = (key: keyof TenantFilters, value: unknown) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const clearFilters = () => {
    setFilters({});
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const filteredTenants = useMemo(() => {
    let filtered = [...tenants];
    
    if (filters.search) {
      filtered = filtered.filter(tenant => 
        tenant.tenant_name.toLowerCase().includes(filters.search!.toLowerCase()) ||
        tenant.tenant_name.toLowerCase().includes(filters.search!.toLowerCase())
      );
    }
    
    return filtered;
  }, [tenants, filters]);

  useEffect(() => {
    loadTenants();
  }, [pagination.page, pagination.limit, filters]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const canPerformAction = (action: string): boolean => {
    if (!adminAuth.hasRole('support_admin')) {
      return false;
    }
    return true;
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-medium text-foreground">Tenant Control Center</h1>
              <p className="text-sm text-muted-foreground">Manage all platform tenants</p>
            </div>
            <div className="flex items-center gap-4">
              <Button
                onClick={() => loadTenants()}
                disabled={loading}
                variant="outline"
                size="sm"
                className="border-slate-700 text-foreground hover:bg-slate-800"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <Card className="border-slate-800 bg-slate-900/50 mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search tenants..."
                  value={filters.search || ''}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10 bg-slate-800/50 border-slate-700 text-foreground placeholder:text-muted-foreground"
                />
              </div>
              
              <Select
                value={filters.plan?.[0] || ''}
                onValueChange={(value) => handleFilterChange('plan', value ? [value] : undefined)}
              >
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-foreground">
                  <SelectValue placeholder="All Plans" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plans</SelectItem>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.status?.[0] || ''}
                onValueChange={(value) => handleFilterChange('status', value ? [value] : undefined)}
              >
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-foreground">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex gap-2">
                <Button
                  onClick={clearFilters}
                  variant="outline"
                  size="sm"
                  className="border-slate-700 text-foreground hover:bg-slate-800"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-slate-800 bg-slate-900/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-slate-800/50">
                  <Building2 className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Tenants</p>
                  <p className="text-2xl font-medium text-foreground">{pagination.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-900/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-slate-800/50">
                  <CreditCard className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active Subscriptions</p>
                  <p className="text-2xl font-medium text-foreground">
                    {filteredTenants.filter(t => t.subscription_status === 'active').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-900/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-slate-800/50">
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">High Churn Risk</p>
                  <p className="text-2xl font-medium text-foreground">
                    {filteredTenants.filter(t => t.churn_risk > 70).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-900/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-slate-800/50">
                  <Zap className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">High AI Usage</p>
                  <p className="text-2xl font-medium text-foreground">
                    {filteredTenants.filter(t => t.ai_cost_today > 100).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tenants Table */}
        <Card className="border-slate-800/60 bg-slate-900/40 backdrop-blur-md shadow-2xl rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-slate-950/50 border-b border-slate-800/60">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-muted-foreground font-medium uppercase tracking-widest text-[10px] py-4">Tenant</TableHead>
                    <TableHead className="text-muted-foreground font-medium uppercase tracking-widest text-[10px] py-4">Industry</TableHead>
                    <TableHead className="text-muted-foreground font-medium uppercase tracking-widest text-[10px] py-4">Plan</TableHead>
                    <TableHead className="text-muted-foreground font-medium uppercase tracking-widest text-[10px] py-4">Billing</TableHead>
                    <TableHead className="text-muted-foreground font-medium uppercase tracking-widest text-[10px] py-4">Users</TableHead>
                    <TableHead className="text-muted-foreground font-medium uppercase tracking-widest text-[10px] py-4">AI Usage (Mo)</TableHead>
                    <TableHead className="text-muted-foreground font-medium uppercase tracking-widest text-[10px] py-4">Created</TableHead>
                    <TableHead className="text-muted-foreground font-medium uppercase tracking-widest text-[10px] py-4">Health</TableHead>
                    <TableHead className="text-muted-foreground font-medium uppercase tracking-widest text-[10px] py-4 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12">
                        <RefreshCw className="h-6 w-6 animate-spin text-emerald-500 mx-auto mb-3" />
                        <p className="text-muted-foreground text-sm font-medium">Loading platform tenants...</p>
                      </TableCell>
                    </TableRow>
                  ) : filteredTenants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12">
                        <Building2 className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground text-sm font-medium">No tenants found</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTenants.map((tenant) => {
                      const tag = (tenant.tenant_name.replace(/[^a-zA-Z0-9]/g, '').substring(0, 8) + '-' + tenant.tenant_id.substring(0, 4)).toUpperCase();
                      return (
                      <TableRow key={tenant.tenant_id} className="border-slate-800/40 hover:bg-slate-800/40 transition-colors group">
                        <TableCell className="py-4">
                          <div className="flex flex-col">
                            <span className="font-medium text-foreground group-hover:text-emerald-400 transition-colors">{tenant.tenant_name}</span>
                            <span className="text-[10px] font-mono text-muted-foreground font-medium tracking-wider">{tag}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-slate-700/50 text-slate-300 border-slate-600/30 text-[10px] uppercase tracking-widest font-medium px-2 py-0.5">
                            {tenant.primary_industry_slug 
                              ? tenant.primary_industry_slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') 
                              : 'Not Set'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${PLAN_COLORS[tenant.plan as keyof typeof PLAN_COLORS]} text-[10px] uppercase tracking-widest font-medium px-2 py-0.5`}>
                            {tenant.plan}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${STATUS_COLORS[tenant.subscription_status as keyof typeof STATUS_COLORS]} text-[10px] uppercase tracking-widest font-medium px-2 py-0.5`}>
                            {tenant.subscription_status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Users className="h-3 w-3 text-muted-foreground" />
                            <span className="text-slate-300 text-sm font-medium">{tenant.total_users}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Zap className="h-3 w-3 text-indigo-400" />
                            <span className="text-slate-300 text-sm font-medium">{formatCurrency(tenant.monthly_ai_cost)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-muted-foreground font-medium">
                            {new Date(tenant.tenant_created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className={`text-sm font-semibold ${
                              tenant.health_score >= 90 ? HEALTH_COLORS.high :
                              tenant.health_score >= 70 ? HEALTH_COLORS.medium :
                              HEALTH_COLORS.low
                            }`}>
                              {tenant.health_score}
                            </div>
                            {tenant.health_score < 70 && (
                              <AlertTriangle className="h-3 w-3 text-yellow-500 animate-pulse" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground hover:bg-slate-700/50"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-slate-800 border-slate-700">
                              <DropdownMenuItem
                                onClick={() => setSelectedTenant(tenant)}
                                className="text-slate-300 hover:text-foreground hover:bg-slate-700"
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </DropdownMenuItem>

                              {tenant.plan !== 'pro' && tenant.plan !== 'enterprise' && (
                                <DropdownMenuItem
                                  onClick={() => handleTenantAction(tenant.tenant_id, 'upgrade_pro')}
                                  disabled={actionLoading === `${tenant.tenant_id}-upgrade_pro`}
                                  className="text-amber-400 hover:text-amber-300 hover:bg-slate-700 font-medium"
                                >
                                  <Zap className="h-4 w-4 mr-2" />
                                  Upgrade to Pro
                                </DropdownMenuItem>
                              )}
                              
                              {tenant.plan === 'pro' && (
                                <DropdownMenuItem 
                                  onClick={() => handleTenantAction(tenant.tenant_id, 'downgrade_starter')}
                                  disabled={actionLoading === `${tenant.tenant_id}-downgrade_starter`}
                                  className="text-slate-300 hover:text-foreground hover:bg-slate-700"
                                >
                                  <MinusCircle className="mr-2 h-4 w-4 text-muted-foreground" />
                                  Remove Pro (Downgrade)
                                </DropdownMenuItem>
                              )}

                              <DropdownMenuItem 
                                onClick={() => handleOpenIndustryDialog(tenant.tenant_id)}
                                className="text-slate-300 hover:text-foreground hover:bg-slate-700"
                              >
                                <Settings className="mr-2 h-4 w-4 text-muted-foreground" />
                                Change Industry
                              </DropdownMenuItem>
                              
                              <DropdownMenuItem 
                                onClick={() => handleOpenMaxUsersDialog(tenant.tenant_id)}
                                className="text-slate-300 hover:text-foreground hover:bg-slate-700"
                              >
                                <Users className="mr-2 h-4 w-4 text-muted-foreground" />
                                Set Max Users
                              </DropdownMenuItem>

                              {canPerformAction('impersonate') && (
                                <DropdownMenuItem
                                  onClick={() => handleTenantAction(tenant.tenant_id, 'impersonate', 'Admin investigation')}
                                  disabled={actionLoading === `${tenant.tenant_id}-impersonate`}
                                  className="text-slate-300 hover:text-foreground hover:bg-slate-700"
                                >
                                  <Shield className="h-4 w-4 mr-2" />
                                  Impersonate
                                </DropdownMenuItem>
                              )}
                              
                              {canPerformAction('suspend') && tenant.subscription_status === 'active' && (
                                <DropdownMenuItem
                                  onClick={() => handleTenantAction(tenant.tenant_id, 'suspend', 'Admin suspension')}
                                  disabled={actionLoading === `${tenant.tenant_id}-suspend`}
                                  className="text-slate-300 hover:text-foreground hover:bg-slate-700"
                                >
                                  <Ban className="h-4 w-4 mr-2" />
                                  Suspend
                                </DropdownMenuItem>
                              )}
                              
                              {canPerformAction('activate') && tenant.subscription_status === 'inactive' && (
                                <DropdownMenuItem
                                  onClick={() => handleTenantAction(tenant.tenant_id, 'activate')}
                                  disabled={actionLoading === `${tenant.tenant_id}-activate`}
                                  className="text-slate-300 hover:text-foreground hover:bg-slate-700"
                                >
                                  <CheckCircle2 className="h-4 w-4 mr-2" />
                                  Activate
                                </DropdownMenuItem>
                              )}
                              
                              {canPerformAction('force_logout') && (
                                <DropdownMenuItem
                                  onClick={() => handleTenantAction(tenant.tenant_id, 'force_logout')}
                                  disabled={actionLoading === `${tenant.tenant_id}-force_logout`}
                                  className="text-slate-300 hover:text-foreground hover:bg-slate-700"
                                >
                                  <LogOut className="h-4 w-4 mr-2" />
                                  Force Logout
                                </DropdownMenuItem>
                              )}
                              
                              {canPerformAction('sync_billing') && (
                                <DropdownMenuItem
                                  onClick={() => handleTenantAction(tenant.tenant_id, 'sync_billing')}
                                  disabled={actionLoading === `${tenant.tenant_id}-sync_billing`}
                                  className="text-slate-300 hover:text-foreground hover:bg-slate-700"
                                >
                                  <RefreshCw className="h-4 w-4 mr-2" />
                                  Sync Billing
                                </DropdownMenuItem>
                              )}
                              
                              {canPerformAction('export') && (
                                <DropdownMenuItem
                                  onClick={() => handleTenantAction(tenant.tenant_id, 'export')}
                                  disabled={actionLoading === `${tenant.tenant_id}-export`}
                                  className="text-slate-300 hover:text-foreground hover:bg-slate-700"
                                >
                                  <Download className="h-4 w-4 mr-2" />
                                  Export Data
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-muted-foreground">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} tenants
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                variant="outline"
                size="sm"
                className="border-slate-700 text-foreground hover:bg-slate-800"
              >
                Previous
              </Button>
              <span className="text-muted-foreground">
                Page {pagination.page} of {pagination.total_pages}
              </span>
              <Button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.total_pages, prev.page + 1) }))}
                disabled={pagination.page === pagination.total_pages}
                variant="outline"
                size="sm"
                className="border-slate-700 text-foreground hover:bg-slate-800"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
      
      {/* Industry Dialog */}
      <Dialog open={isIndustryDialogOpen} onOpenChange={setIsIndustryDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-foreground">Change Tenant Industry</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Select Industry</label>
              <Select value={newIndustry} onValueChange={setNewIndustry}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-foreground">
                  <SelectValue placeholder="Select an industry..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="interior-design">Interior Design</SelectItem>
                  <SelectItem value="architecture">Architecture</SelectItem>
                  <SelectItem value="construction">Construction</SelectItem>
                  <SelectItem value="software">Software</SelectItem>
                  <SelectItem value="consulting">Consulting</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setIsIndustryDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={submitIndustryChange}
                disabled={!newIndustry || actionLoading === `${actionTenantId}-industry`}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {actionLoading === `${actionTenantId}-industry` ? 'Saving...' : 'Save Industry'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Max Users Dialog */}
      <Dialog open={isMaxUsersDialogOpen} onOpenChange={setIsMaxUsersDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-foreground">Set Custom Max Users</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Max Users (Number)</label>
              <Input 
                type="number"
                min="1"
                placeholder="e.g. 15"
                value={newMaxUsers}
                onChange={(e) => setNewMaxUsers(e.target.value ? parseInt(e.target.value, 10) : '')}
                className="bg-slate-800 border-slate-700 text-foreground"
              />
              <p className="text-xs text-muted-foreground">Overrides the default plan limits (1 for Starter, 10 for Pro).</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setIsMaxUsersDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={submitMaxUsersChange}
                disabled={newMaxUsers === '' || actionLoading === `${actionTenantId}-max_users`}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {actionLoading === `${actionTenantId}-max_users` ? 'Saving...' : 'Save Limit'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
